import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { useCallback } from 'react'
import { useFormikContext } from 'formik'
import { useCreateOppActions } from '@user/components/Location'
import { ILeadFormModel } from '../typings'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { getTouchedObject } from '@sales-center/common'
import { WebFormHeader } from '@user/features/CampaignTracking/WebForm/WebFormHeader/WebFormHeader'
import { DraftLead } from '@user/store/draftLeads'
import { EditMatchedLocationHeaderAddLead } from '@user/components/Location/AllowEditMatchedLocation'

import { useData } from './hooks'
import { ShowRequiredFieldsToggle, SubHeader } from '../FormComponents'
import useMediaQuery from '@mui/material/useMediaQuery'
import { NoteCode } from '@user/components/Notes/typings'
import { FieldSettingsMap } from '@user/store/entityFormSettings'
import { useNonRequiredFieldValidate } from '@user/components/Leads/AddLeadPage/AddLeadHeader/hooks/useNonRequiredFieldValidate'
import { AddToPestPacToggle } from '../FormComponents/AddToPestPacToggle/AddToPestPacToggle'
import { useSelector } from 'react-redux'
import { selectPestPacLocationDuplicatesFilters } from '@user/store/duplicates/PestPacLocations/slice'
import DuplicatesFoundDialog from '@user/components/Leads/AddLeadPage/FormComponents/DuplicateAlert/DuplicatesFoundDialog'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

export interface Props {
  backgroundColor?: string
  onCancel: () => void
  hideOptional: boolean
  onChangeHideOptional: (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void
  createOppActions: ReturnType<typeof useCreateOppActions>
  draftLeadId?: number | null
  type: string
  isDraftLead: boolean
  draftLead?: DraftLead
  isFromLocation: boolean
  isMappedWithUpdateLocationPerm?: boolean
  noteCodes: NoteCode[]
  setHideOptional: (status: boolean) => void
  entitySettingsMap: FieldSettingsMap
  activeAddToPestPac: boolean
  fromMapView?: boolean
  onClose?: () => void
  prepareSubmitValidation: () => void
}

const AddLeadHeader: React.FC<Props> = (props) => {
  const theme = useTheme()
  const {
    backgroundColor = theme.palette.common.white,
    draftLead,
    hideOptional,
    isDraftLead,
    isFromLocation,
    isMappedWithUpdateLocationPerm,
    noteCodes,
    onCancel,
    setHideOptional,
    entitySettingsMap,
    activeAddToPestPac,
    fromMapView = false,
    onClose,
    prepareSubmitValidation,
  } = props

  const { disableSaveAndView } = useData()

  const {
    submitForm,
    isSubmitting,
    setFieldValue,
    setTouched,
    errors,
    values,
    isValid,
  } = useFormikContext<ILeadFormModel>()

  const { externalLocationId } = values

  const { checkNonRequiredErrors } = useNonRequiredFieldValidate({
    setHideOptional,
    settings: entitySettingsMap,
    errors,
    values,
  })

  const [confirmOpen, setConfirmOpen] = React.useState(false)

  const { resultCount: pestPacDuplicateCount } = useSelector(
    selectPestPacLocationDuplicatesFilters,
  )

  const saveForm = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setFieldValue(
        'viewDetail',
        event.currentTarget.innerText.indexOf('View') !== -1,
      )
      const nonRequiredHasErrors = checkNonRequiredErrors()
      if (!nonRequiredHasErrors) {
        const pestPacDuplicateFound =
          values.createNewLocation && pestPacDuplicateCount > 0
        if (pestPacDuplicateFound && isValid) {
          setConfirmOpen(true)
        } else {
          prepareSubmitValidation()
          submitForm()
        }
      } else {
        setTouched(getTouchedObject(errors))
      }
    },
    [
      setFieldValue,
      checkNonRequiredErrors,
      setTouched,
      errors,
      values.createNewLocation,
      pestPacDuplicateCount,
      isValid,
      prepareSubmitValidation,
      submitForm,
    ],
  )

  const onProceedAfterConfirm = useCallback(() => {
    setConfirmOpen(false)
    prepareSubmitValidation()
    submitForm()
  }, [prepareSubmitValidation, submitForm])

  const onReviewDuplicates = useCallback(() => {
    setConfirmOpen(false)
    window.dispatchEvent(new CustomEvent('open-duplicates-dialog'))
  }, [])

  const isMobile = useMediaQuery('(max-width:900px)') && !fromMapView

  return (
    <React.Fragment>
      <Box
        sx={
          fromMapView
            ? {
                position: 'sticky',
                top: 0,
                zIndex: theme.zIndex.appBar,
                bgcolor: theme.palette.common.white,
              }
            : undefined
        }
      >
        <Paper
          square
          elevation={0}
          sx={{
            padding: theme.spacing(1),
            flexWrap: 'nowrap',
            backgroundColor: { backgroundColor },
            borderBottom: '1px solid #e0e0e0',
            paddingLeft: theme.spacing(2),
          }}
        >
          <Box display="flex" ml={0} sx={{ alignItems: 'center' }}>
            {fromMapView ? (
              <Typography variant="h7">Add Lead</Typography>
            ) : (
              <>
                <ShowRequiredFieldsToggle {...props} />

                {!isMobile && activeAddToPestPac && (
                  <Box display="flex" flexGrow={2}>
                    <AddToPestPacToggle />
                  </Box>
                )}
              </>
            )}

            <Button
              onClick={fromMapView ? onClose : onCancel}
              style={{ marginLeft: 'auto' }}
              variant="text"
              data-test-id="cancelLeadBtn"
              sx={{ marginLeft: theme.spacing(1) }}
            >
              Cancel
            </Button>
            {!disableSaveAndView && !fromMapView && (
              <Button
                disabled={isSubmitting}
                variant="contained"
                onClick={saveForm}
                data-test-id="saveAndViewLeadBtn"
                sx={{ marginLeft: theme.spacing(1) }}
              >
                Save and View
              </Button>
            )}

            <Button
              disabled={isSubmitting}
              onClick={saveForm}
              variant="contained"
              color="primary"
              data-test-id="saveLeadBtn"
              sx={{ marginLeft: theme.spacing(1) }}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Box>
      {fromMapView && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignContent={{ xs: 'flex-start', sm: 'center' }}
          p={1.5}
        >
          <ShowRequiredFieldsToggle {...props} />

          {!isMobile && activeAddToPestPac && (
            <Box display="flex" flexGrow={2}>
              <AddToPestPacToggle />
            </Box>
          )}
        </Stack>
      )}

      {isDraftLead && <WebFormHeader draftLead={draftLead} />}
      {isFromLocation && isMappedWithUpdateLocationPerm && (
        <EditMatchedLocationHeaderAddLead
          externalLocationId={externalLocationId ?? ''}
          isBillTo={false}
        />
      )}
      {isMobile && (
        <SubHeader hideOptional={hideOptional} noteCodes={noteCodes} />
      )}
      <DuplicatesFoundDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onProceed={onProceedAfterConfirm}
        onReviewDuplicates={onReviewDuplicates}
      />
    </React.Fragment>
  )
}
AddLeadHeader.displayName = 'AddLeadHeader'

export default React.memo(AddLeadHeader)
